import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bot, ArrowUp, Loader2, Plus, MessageSquare, Trash2,
  PanelLeft, Sparkles, X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import ToolResultCard from '../../components/ai-agent/ToolResultCard';
import ConfirmActionCard from '../../components/ai-agent/ConfirmActionCard';
import BriefingPanel from '../../components/ai-agent/BriefingPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AIAgentPage — chat-first interface for the DCCF AI Agent.
 *
 * Design intent
 *   • Empty state is a centered welcome + composer. No suggestion cards.
 *   • Daily Briefing is accessible from a header pill (opens as a sheet).
 *   • History sidebar collapses on desktop and becomes a sheet on mobile.
 *   • Chat occupies the full viewport; branding is in typography, not boxes.
 *
 * All functionality is preserved: tool results, confirmation cards,
 * conversation sessions, image generation, permission enforcement, audit.
 */
const AIAgentPage = () => {
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [briefing, setBriefing] = useState(null);
  const [briefingOpen, setBriefingOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState({});
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ai-agent/sessions`, { withCredentials: true });
      setSessions(data.sessions || []);
    } catch {
      setSessions([]);
    }
  }, []);

  const loadSession = useCallback(async (id) => {
    if (!id) {
      setSessionId('');
      setMessages([]);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/ai-agent/sessions/${id}`, { withCredentials: true });
      setSessionId(id);
      setMessages(data.messages || []);
      setHistoryOpen(false);
      setTimeout(scrollToBottom, 0);
    } catch {
      toast.error('Could not load conversation');
    }
  }, [scrollToBottom]);

  const loadBriefing = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ai-agent/briefing`, { withCredentials: true });
      setBriefing(data);
    } catch {
      /* non-fatal */
    }
  }, []);

  useEffect(() => {
    loadSessions();
    loadBriefing();
  }, [loadSessions, loadBriefing]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Auto-grow the composer textarea
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [input]);

  const send = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput('');
    const localUserMsg = {
      id: `local-${Date.now()}`,
      role: 'user',
      content: msg,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, localUserMsg]);
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/ai-agent/chat`,
        { message: msg, session_id: sessionId || undefined },
        { withCredentials: true, timeout: 120000 },
      );
      const newSessionId = data.session_id;
      if (!sessionId && newSessionId) setSessionId(newSessionId);
      const sess = await axios.get(`${API}/ai-agent/sessions/${newSessionId}`, { withCredentials: true });
      setMessages(sess.data.messages || []);
      loadSessions();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'The agent failed to respond — please try again.');
      setMessages((prev) => [...prev, {
        id: `err-${Date.now()}`, role: 'assistant', content: detail || 'Request failed.', error: true,
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
    }
  }, [input, sessionId, sending, loadSessions]);

  const startNewSession = () => {
    setSessionId('');
    setMessages([]);
    setHistoryOpen(false);
    setInput('');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const deleteSession = async (id, e) => {
    e?.stopPropagation?.();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await axios.delete(`${API}/ai-agent/sessions/${id}`, { withCredentials: true });
      if (sessionId === id) startNewSession();
      loadSessions();
    } catch {
      toast.error('Could not delete conversation');
    }
  };

  const confirmPending = async (toolMsgId, pendingId) => {
    setConfirmBusy((prev) => ({ ...prev, [toolMsgId]: 'running' }));
    try {
      const { data } = await axios.post(
        `${API}/ai-agent/confirm`,
        { pending_id: pendingId },
        { withCredentials: true, timeout: 120000 },
      );
      setConfirmBusy((prev) => ({ ...prev, [toolMsgId]: data.ok ? 'done-success' : 'done-fail' }));
      if (data.ok) toast.success('Action executed');
      else toast.error(data.error || 'Action failed');
      const sess = await axios.get(`${API}/ai-agent/sessions/${sessionId}`, { withCredentials: true });
      setMessages(sess.data.messages || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not execute action');
      setConfirmBusy((prev) => ({ ...prev, [toolMsgId]: 'done-fail' }));
    }
  };

  const noAccess = user && !(user.role === 'super_admin' || user.ai_agent_access);
  const grouped = React.useMemo(() => _groupMessages(messages), [messages]);
  const hasConversation = grouped.length > 0 || sending;

  if (noAccess) {
    return (
      <div className="max-w-xl mx-auto p-6 text-center" data-testid="ai-agent-no-access">
        <Bot className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <div className="font-bold text-[#1a1a1a]">AI Agent is not enabled on your account</div>
        <div className="text-sm text-slate-500 mt-1">
          Ask a Super Admin to enable AI Agent access for you under User Management.
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col h-[calc(100vh-5rem)] sm:h-[calc(100vh-6rem)] bg-white"
      data-testid="ai-agent-page"
    >
      {/* ─── Header ───────────────────────────────────────────── */}
      <header className="flex items-center gap-3 px-3 sm:px-5 h-12 border-b border-slate-100 flex-shrink-0">
        <button
          onClick={() => setHistoryOpen(true)}
          className="p-1.5 rounded-md text-slate-500 hover:text-[#002249] hover:bg-slate-50 transition-colors"
          data-testid="ai-open-history"
          aria-label="Open conversation history"
        >
          <PanelLeft className="w-[18px] h-[18px]" />
        </button>

        <div className="flex items-center gap-1.5 min-w-0">
          <Bot className="w-[18px] h-[18px] text-[#002249]" />
          <span className="font-semibold text-[14.5px] text-[#1a1a1a] tracking-tight">
            DCCF AI
          </span>
        </div>

        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={() => setBriefingOpen(true)}
            className="px-2.5 py-1.5 text-[12px] font-semibold text-slate-600 hover:text-[#002249] hover:bg-slate-50 rounded-md transition-colors inline-flex items-center gap-1.5"
            data-testid="ai-open-briefing"
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            Briefing
          </button>
          <button
            onClick={startNewSession}
            className="px-2.5 py-1.5 text-[12px] font-semibold text-slate-600 hover:text-[#002249] hover:bg-slate-50 rounded-md transition-colors inline-flex items-center gap-1.5"
            data-testid="ai-new-session"
          >
            <Plus className="w-3.5 h-3.5" />
            New
          </button>
        </div>
      </header>

      {/* ─── Main stage ───────────────────────────────────────── */}
      {!hasConversation ? (
        <WelcomeState
          input={input}
          setInput={setInput}
          send={send}
          sending={sending}
          inputRef={inputRef}
          userName={user?.name?.split(' ')?.[0]}
        />
      ) : (
        <>
          <main
            ref={scrollRef}
            className="flex-1 overflow-y-auto overscroll-contain"
            data-testid="ai-chat-stage"
          >
            <div className="max-w-2xl mx-auto px-3 sm:px-5 py-6 pb-10 space-y-5">
              {grouped.map((turn, idx) => (
                <TurnBubble
                  key={turn.key || idx}
                  turn={turn}
                  confirmBusy={confirmBusy}
                  onConfirm={(toolMsgId, pendingId) => confirmPending(toolMsgId, pendingId)}
                />
              ))}
              {sending && <ThinkingIndicator />}
            </div>
          </main>

          {/* Sticky composer while chatting */}
          <div
            className="flex-shrink-0 border-t border-slate-100 bg-white/80 backdrop-blur-sm px-3 sm:px-5 py-3"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
          >
            <div className="max-w-2xl mx-auto">
              <Composer
                input={input}
                setInput={setInput}
                send={send}
                sending={sending}
                inputRef={inputRef}
              />
            </div>
          </div>
        </>
      )}

      {/* ─── History sheet / sidebar ─────────────────────────── */}
      <HistorySheet
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        sessions={sessions}
        currentId={sessionId}
        onPick={loadSession}
        onNew={startNewSession}
        onDelete={deleteSession}
      />

      {/* ─── Briefing sheet ──────────────────────────────────── */}
      <BriefingSheet
        open={briefingOpen}
        onClose={() => setBriefingOpen(false)}
        data={briefing}
        onAsk={(text) => {
          setBriefingOpen(false);
          send(text);
        }}
      />
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════
// Welcome state (empty chat)
// ═════════════════════════════════════════════════════════════════════

const WelcomeState = ({ input, setInput, send, sending, inputRef, userName }) => (
  <main
    className="flex-1 flex flex-col items-center justify-center overflow-y-auto px-4"
    data-testid="ai-welcome-state"
  >
    <div className="w-full max-w-xl flex flex-col items-center gap-6 py-10">
      <div className="w-11 h-11 rounded-2xl bg-[#002249] text-white flex items-center justify-center shadow-sm">
        <Bot className="w-5 h-5" />
      </div>
      <h1 className="text-[22px] sm:text-[26px] font-semibold text-[#1a1a1a] text-center tracking-tight">
        {userName ? `How can I help you today, ${userName}?` : 'How can I help you today?'}
      </h1>
      <div className="w-full">
        <Composer
          input={input}
          setInput={setInput}
          send={send}
          sending={sending}
          inputRef={inputRef}
          autoFocus
        />
      </div>
    </div>
  </main>
);

// ═════════════════════════════════════════════════════════════════════
// Composer
// ═════════════════════════════════════════════════════════════════════

const Composer = ({ input, setInput, send, sending, inputRef, autoFocus }) => {
  const disabled = sending || !input.trim();
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); send(); }}
      className="relative rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03),0_6px_20px_rgba(0,34,73,0.04)] focus-within:border-slate-300 transition-colors"
    >
      <textarea
        ref={inputRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Ask DCCF AI anything…"
        rows={1}
        autoFocus={autoFocus}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send();
          }
        }}
        className="w-full resize-none border-0 outline-none bg-transparent px-4 pt-3.5 pb-12 text-[15px] leading-6 placeholder-slate-400 focus:outline-none focus:ring-0 max-h-40"
        data-testid="ai-chat-input"
      />
      <button
        type="submit"
        disabled={disabled}
        className={`absolute right-2 bottom-2 w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
          disabled
            ? 'bg-slate-100 text-slate-300'
            : 'bg-[#002249] text-white hover:bg-[#0a3570] shadow-sm'
        }`}
        data-testid="ai-send-btn"
        aria-label="Send message"
      >
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
      </button>
    </form>
  );
};

// ═════════════════════════════════════════════════════════════════════
// Conversation rendering
// ═════════════════════════════════════════════════════════════════════

function _groupMessages(messages) {
  const out = [];
  let current = null;
  for (const m of messages) {
    if (m.role === 'user') {
      if (current) out.push(current);
      current = { user: m, tools: [], assistant: null, key: m.id };
    } else if (m.role === 'tool') {
      if (!current) current = { user: null, tools: [], assistant: null, key: m.id };
      current.tools.push(m);
    } else if (m.role === 'assistant') {
      if (!current) current = { user: null, tools: [], assistant: null, key: m.id };
      current.assistant = m;
      out.push(current);
      current = null;
    }
  }
  if (current) out.push(current);
  return out;
}

const TurnBubble = ({ turn, confirmBusy, onConfirm }) => (
  <div className="space-y-3">
    {turn.user && (
      <div className="flex justify-end">
        <div
          className="max-w-[85%] bg-[#002249] text-white rounded-2xl rounded-br-md px-4 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap"
          data-testid="ai-user-bubble"
        >
          {turn.user.content}
        </div>
      </div>
    )}
    {turn.tools.map((t) => (
      <React.Fragment key={t.id}>
        <ToolResultCard card={_toolMsgToCard(t)} />
        {t.requires_confirmation && !t.confirmed && t.pending_id && (
          <ConfirmActionCard
            pending={{ tool_id: t.tool_id, preview: t.pending_preview }}
            onConfirm={() => onConfirm(t.id, t.pending_id)}
            onCancel={() => {}}
            busy={confirmBusy[t.id] === 'running'}
            done={confirmBusy[t.id]?.startsWith('done-')}
            success={confirmBusy[t.id] === 'done-success'}
          />
        )}
      </React.Fragment>
    ))}
    {turn.assistant && (
      <div className="flex items-start gap-2.5">
        <div className="w-6 h-6 rounded-md bg-[#002249] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
          <Bot className="w-3.5 h-3.5" />
        </div>
        <div
          className={`flex-1 min-w-0 text-[14px] leading-relaxed whitespace-pre-wrap text-slate-800 ${
            turn.assistant.error ? 'text-rose-700' : ''
          }`}
          data-testid="ai-assistant-bubble"
        >
          {turn.assistant.content}
        </div>
      </div>
    )}
  </div>
);

function _toolMsgToCard(t) {
  return {
    tool_id: t.tool_id,
    ok: t.ok,
    risk: t.risk,
    requires_confirmation: t.requires_confirmation,
    pending_preview: t.pending_preview,
    result: t.result,
    error: t.error,
  };
}

const ThinkingIndicator = () => (
  <div className="flex items-start gap-2.5 pt-1" data-testid="ai-thinking">
    <div className="w-6 h-6 rounded-md bg-[#002249] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
      <Bot className="w-3.5 h-3.5" />
    </div>
    <div className="flex items-center gap-1 pt-2">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" style={{ animationDelay: '0ms' }} />
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" style={{ animationDelay: '150ms' }} />
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" style={{ animationDelay: '300ms' }} />
    </div>
  </div>
);

// ═════════════════════════════════════════════════════════════════════
// History sheet / sidebar
// ═════════════════════════════════════════════════════════════════════

const HistorySheet = ({ open, onClose, sessions, currentId, onPick, onNew, onDelete }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40"
      onClick={onClose}
      data-testid="ai-history-sheet"
    >
      <aside
        className="absolute top-0 left-0 bottom-0 w-[82%] max-w-xs bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 h-12 border-b border-slate-100 flex-shrink-0">
          <div className="font-semibold text-[13px] text-[#1a1a1a]">Conversations</div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-slate-500 hover:bg-slate-50"
            data-testid="ai-close-history"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-3 flex-shrink-0">
          <button
            onClick={onNew}
            className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg bg-[#002249] hover:bg-[#0a3570] text-white text-[12.5px] font-semibold transition-colors"
            data-testid="ai-history-new"
          >
            <Plus className="w-3.5 h-3.5" /> New conversation
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-4">
          {sessions.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-6">No conversations yet.</div>
          ) : (
            <div className="space-y-0.5">
              {sessions.map((s) => (
                <div
                  key={s.id}
                  onClick={() => onPick(s.id)}
                  className={`group flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer text-[12.5px] ${
                    s.id === currentId
                      ? 'bg-[#002249] text-white'
                      : 'hover:bg-slate-50 text-slate-700'
                  }`}
                  data-testid={`ai-session-${s.id}`}
                >
                  <MessageSquare className="w-3.5 h-3.5 flex-shrink-0 opacity-70" />
                  <div className="flex-1 truncate">{s.title || 'Conversation'}</div>
                  <button
                    onClick={(e) => onDelete(s.id, e)}
                    className={`opacity-0 group-hover:opacity-100 transition-opacity ${s.id === currentId ? 'text-white/70 hover:text-white' : 'text-slate-400 hover:text-rose-500'}`}
                    data-testid={`ai-session-delete-${s.id}`}
                    aria-label="Delete conversation"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════
// Briefing sheet
// ═════════════════════════════════════════════════════════════════════

const BriefingSheet = ({ open, onClose, data, onAsk }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40"
      onClick={onClose}
      data-testid="ai-briefing-sheet"
    >
      <aside
        className="absolute top-0 right-0 bottom-0 w-full sm:w-[520px] bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 h-12 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-1.5 font-semibold text-[13px] text-[#1a1a1a]">
            <Sparkles className="w-3.5 h-3.5 text-amber-500" /> Daily Briefing
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-slate-500 hover:bg-slate-50"
            data-testid="ai-close-briefing"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {data ? (
            <BriefingPanel data={data} onAsk={onAsk} />
          ) : (
            <div className="text-xs text-slate-400 text-center py-10">Briefing not available.</div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default AIAgentPage;
